import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { NavGroupKey } from '@/config/navigation';

const COLLAPSED_STORAGE_KEY = 'candeza-sidebar-collapsed';
/**
 * Stores the COLLAPSED group keys — a group is expanded unless it appears
 * here, so new groups default open. The key was originally named
 * `…-expanded-groups`, which described the exact opposite of its contents;
 * `LEGACY_…` below migrates anyone who already has that key so the rename
 * doesn't silently reset their sidebar.
 */
const COLLAPSED_GROUPS_STORAGE_KEY = 'candeza-sidebar-collapsed-groups';
const LEGACY_COLLAPSED_GROUPS_STORAGE_KEY = 'candeza-sidebar-expanded-groups';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function readCollapsedGroups(): Set<NavGroupKey> {
  try {
    const raw =
      localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_COLLAPSED_GROUPS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed as NavGroupKey[]);
  } catch {
    return new Set();
  }
}

interface SidebarContextType {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  toggleCollapse: () => void;
  toggleMobile: () => void;
  closeMobile: () => void;
  /** A group is expanded unless explicitly collapsed — new groups default open. */
  isGroupExpanded: (group: NavGroupKey) => boolean;
  toggleGroup: (group: NavGroupKey) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(readCollapsed);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<NavGroupKey>>(readCollapsedGroups);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* localStorage unavailable (private browsing) — collapse still works, just not persisted */
      }
      return next;
    });
  };

  const toggleMobile = () => setIsMobileOpen((prev) => !prev);
  const closeMobile = () => setIsMobileOpen(false);

  const isGroupExpanded = useCallback(
    (group: NavGroupKey) => !collapsedGroups.has(group),
    [collapsedGroups],
  );

  const toggleGroup = useCallback((group: NavGroupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      try {
        localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify([...next]));
        localStorage.removeItem(LEGACY_COLLAPSED_GROUPS_STORAGE_KEY);
      } catch {
        /* localStorage unavailable — group toggle still works, just not persisted */
      }
      return next;
    });
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        isMobileOpen,
        toggleCollapse,
        toggleMobile,
        closeMobile,
        isGroupExpanded,
        toggleGroup,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
